import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import NotificationBell from "@/components/NotificationBell";
import Logo, { LogoMark } from "@/components/Logo";
import { Boxes, CalendarClock, ClipboardList, LifeBuoy, LogOut, ShieldCheck, Wallet } from "lucide-react";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  const items = [
    { to: "/app/projects", key: "projects", icon: Boxes, testid: "nav-projects" },
    { to: "/app/timeline", key: "timeline", icon: CalendarClock, testid: "nav-timeline" },
    { to: "/app/snags",    key: "snags",    icon: ClipboardList, testid: "nav-snags" },
    { to: "/app/billing",  key: "billing",  icon: Wallet, testid: "nav-billing" },
    { to: "/app/support",  key: "support",  icon: LifeBuoy, testid: "nav-support" },
    ...(user?.role === "admin"
      ? [{ to: "/app/admin", key: "admin", icon: ShieldCheck, testid: "nav-admin" }]
      : []),
  ];

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[240px_1fr] bg-[#FAF9F4]">
      <aside className="hidden lg:flex flex-col border-r border-neutral-200 bg-black">
        <div className="p-6 border-b border-white/10">
          <Logo size={24} inverse />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map(({ to, key, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#B8933A] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] text-neutral-500 truncate flex-1" data-testid="sidebar-user-email">{user?.email}</div>
            {(user?.plan === "bronze" || user?.plan === "gold") && (
              <span
                className={`ml-2 text-[9px] font-bold tracking-widest text-black px-1.5 py-0.5 rounded ${
                  user.plan === "gold" ? "bg-[#B8933A]" : "bg-[#B87333] text-white"
                }`}
                data-testid="sidebar-pro-badge">
                {user.plan.toUpperCase()}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start mt-2 text-neutral-300 hover:text-white hover:bg-white/5"
            data-testid="logout-button"
            onClick={async () => { await logout(); nav("/"); }}>
            <LogOut className="w-4 h-4 mr-2" />{t("sign_out")}
          </Button>
        </div>
      </aside>

      <div className="flex flex-col min-w-0">
        <header className="glass-header sticky top-0 z-20">
          <div className="px-6 py-3 flex items-center justify-between">
            <div className="lg:hidden">
              <Logo size={22} />
            </div>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-3">
              <NotificationBell />
              <LanguageToggle />
            </div>
          </div>
          {/* mobile nav */}
          <div className="lg:hidden px-3 pb-3 flex gap-2 overflow-x-auto">
            {items.map(({ to, key, icon: Icon, testid }) => (
              <NavLink key={to} to={to} data-testid={`${testid}-m`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded text-xs font-medium whitespace-nowrap ${
                    isActive ? "bg-black text-white" : "text-neutral-600 bg-white border border-neutral-200"
                  }`}>
                <Icon className="w-3.5 h-3.5" />{t(key)}
              </NavLink>
            ))}
            <button onClick={async () => { await logout(); nav("/"); }}
              className="ml-auto px-3 py-2 rounded text-xs bg-white border border-neutral-200 text-neutral-600">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
